import { batch50Portraits } from './castPortraitBatch50.ts'
import { photoPassPortraits } from './castPortraitPhotoPass.ts'
import { researched2021Portraits } from './castPortraitResearch2021.ts'
import { octoberPassPortraits } from './castPortraitOctPass.ts'

export type CastPortrait = {
  personId: string
  name: string
  shortName: string
  initials: string
  platform: 'tiktok' | 'instagram'
  handle: string
  profileUrl: string
  postUrl: string
  postId: string
  mediaKind: 'video' | 'photo'
  showLabel: string
  accessibilitySummary: string
  verificationBasis?: string
  tone: 'berry' | 'violet' | 'citrus' | 'coral' | 'indigo' | 'rose'
  focusY?: number
}

// These are canonical public-post links from cast-owned accounts that were
// identity-checked on or before 2026-10-01. Journey90 never stores the media itself.
const coreCastPortraits: CastPortrait[] = [
  {
    personId: 'p-jasmine-pineda',
    name: 'Jasmine Pineda',
    shortName: 'Jasmine',
    initials: 'JP',
    platform: 'tiktok',
    handle: '@jasminepanama22',
    profileUrl: 'https://www.tiktok.com/@jasminepanama22',
    postUrl: 'https://www.tiktok.com/@jasminepanama22/video/7676150185059175700',
    postId: '7676150185059175700',
    mediaKind: 'video',
    showLabel: 'Before the 90 Days',
    accessibilitySummary: 'A close-up indoor portrait of Jasmine in a neutral beige top.',
    tone: 'berry',
  },
  {
    personId: 'p-kris-foster',
    name: 'Kris Foster',
    shortName: 'Kris',
    initials: 'KF',
    platform: 'instagram',
    handle: '@crazykboog1',
    profileUrl: 'https://www.instagram.com/crazykboog1/',
    postUrl: 'https://www.instagram.com/p/Cr5THk-uF4Q/',
    postId: 'Cr5THk-uF4Q',
    mediaKind: 'photo',
    showLabel: 'The Other Way',
    accessibilitySummary: 'Kris smiles in a selfie showing her shorter, bright pink hairstyle.',
    verificationBasis: 'Cast-specific coverage identifies the creator and describes this exact post as Kris smiling in a selfie with her new shorter hairstyle: https://www.monstersandcritics.com/tv/reality-tv/90-day-fiance-the-other-way-star-kris-foster-debuts-new-shorter-do-what-have-i-done/. The post’s live Instagram media endpoint returned a portrait thumbnail on 2026-10-01.',
    tone: 'coral',
  },
  {
    personId: 'p-gino-palazzolo',
    name: 'Gino Palazzolo',
    shortName: 'Gino',
    initials: 'GP',
    platform: 'tiktok',
    handle: '@ginopalazzolo',
    profileUrl: 'https://www.tiktok.com/@ginopalazzolo',
    postUrl: 'https://www.tiktok.com/@ginopalazzolo/video/7595666072829807902',
    postId: '7595666072829807902',
    mediaKind: 'video',
    showLabel: 'Before the 90 Days',
    accessibilitySummary: 'A solo promotional portrait of Gino in a dark jacket and hat against a warm gold background.',
    tone: 'citrus',
    focusY: 34,
  },
  {
    personId: 'p-yara-zaya',
    name: 'Yara Zaya',
    shortName: 'Yara',
    initials: 'YZ',
    platform: 'tiktok',
    handle: '@yarazaya1',
    profileUrl: 'https://www.tiktok.com/@yarazaya1',
    postUrl: 'https://www.tiktok.com/@yarazaya1/video/7686997410274479374',
    postId: '7686997410274479374',
    mediaKind: 'video',
    showLabel: '90 Day Fiancé',
    accessibilitySummary: 'A bright car-seat portrait of Yara in a blue collared top.',
    tone: 'indigo',
    focusY: 60,
  },
  {
    personId: 'p-jovi-dufren',
    name: 'Jovi Dufren',
    shortName: 'Jovi',
    initials: 'JD',
    platform: 'tiktok',
    handle: '@jovidufren',
    profileUrl: 'https://www.tiktok.com/@jovidufren',
    postUrl: 'https://www.tiktok.com/@jovidufren/video/7685928945241443598',
    postId: '7685928945241443598',
    mediaKind: 'video',
    showLabel: '90 Day Fiancé',
    accessibilitySummary: 'A clear solo close-up portrait of Jovi in a navy shirt indoors.',
    tone: 'violet',
    focusY: 44,
  },
  {
    personId: 'p-paola-mayfield',
    name: 'Paola Mayfield',
    shortName: 'Paola',
    initials: 'PM',
    platform: 'tiktok',
    handle: '@paola_mayfield',
    profileUrl: 'https://www.tiktok.com/@paola_mayfield',
    postUrl: 'https://www.tiktok.com/@paola_mayfield/video/7684421918002302221',
    postId: '7684421918002302221',
    mediaKind: 'video',
    showLabel: '90 Day Fiancé',
    accessibilitySummary: 'A polished head-and-shoulders portrait of Paola.',
    tone: 'coral',
  },
  {
    personId: 'p-russ-mayfield',
    name: 'Russ Mayfield',
    shortName: 'Russ',
    initials: 'RM',
    platform: 'tiktok',
    handle: '@russ_mayfield',
    profileUrl: 'https://www.tiktok.com/@russ_mayfield',
    postUrl: 'https://www.tiktok.com/@russ_mayfield/video/7679495782214438175',
    postId: '7679495782214438175',
    mediaKind: 'video',
    showLabel: '90 Day Fiancé',
    accessibilitySummary: 'A clear solo indoor portrait of Russ in an olive shirt during a haircut video.',
    tone: 'berry',
    focusY: 42,
  },
  {
    personId: 'p-rebecca-parrott',
    name: 'Rebecca Parrott',
    shortName: 'Rebecca',
    initials: 'RP',
    platform: 'tiktok',
    handle: '@tlc90dayrebecca',
    profileUrl: 'https://www.tiktok.com/@tlc90dayrebecca',
    postUrl: 'https://www.tiktok.com/@tlc90dayrebecca/video/7685373359147715853',
    postId: '7685373359147715853',
    mediaKind: 'video',
    showLabel: 'Before the 90 Days',
    accessibilitySummary: 'A smiling solo car portrait of Rebecca.',
    tone: 'violet',
    focusY: 72,
  },
  {
    personId: 'p-sophie-sierra',
    name: 'Sophie Sierra',
    shortName: 'Sophie',
    initials: 'SS',
    platform: 'tiktok',
    handle: '@sophiesierra90day',
    profileUrl: 'https://www.tiktok.com/@sophiesierra90day',
    postUrl: 'https://www.tiktok.com/@sophiesierra90day/video/7686930180970925334',
    postId: '7686930180970925334',
    mediaKind: 'video',
    showLabel: '90 Day Fiancé',
    accessibilitySummary: 'A solo outdoor portrait of Sophie with long dark curly hair.',
    tone: 'rose',
  },
  {
    personId: 'p-chantel-everett',
    name: 'CeAir "Chantel" Everett',
    shortName: 'Chantel',
    initials: 'CE',
    platform: 'tiktok',
    handle: '@chantel_j_',
    profileUrl: 'https://www.tiktok.com/@chantel_j_',
    postUrl: 'https://www.tiktok.com/@chantel_j_/video/7626102595160952077',
    postId: '7626102595160952077',
    mediaKind: 'video',
    showLabel: '90 Day Fiancé',
    accessibilitySummary: 'A clean solo car portrait of Chantel in a white top.',
    tone: 'citrus',
  },
  {
    personId: 'p-thais-ramone',
    name: 'Thaís Ramone',
    shortName: 'Thaís',
    initials: 'TR',
    platform: 'tiktok',
    handle: '@thaisramoniee',
    profileUrl: 'https://www.tiktok.com/@thaisramoniee',
    postUrl: 'https://www.tiktok.com/@thaisramoniee/video/7577469408818236703',
    postId: '7577469408818236703',
    mediaKind: 'video',
    showLabel: '90 Day Fiancé',
    accessibilitySummary: 'A close-up car portrait of Thaís with dark hair.',
    tone: 'berry',
  },
  {
    personId: 'p-ariela-weinberg',
    name: 'Ariela Weinberg',
    shortName: 'Ariela',
    initials: 'AW',
    platform: 'tiktok',
    handle: '@arieladanielle',
    profileUrl: 'https://www.tiktok.com/@arieladanielle',
    postUrl: 'https://www.tiktok.com/@arieladanielle/video/7684670117828939038',
    postId: '7684670117828939038',
    mediaKind: 'video',
    showLabel: 'The Other Way',
    accessibilitySummary: 'A casual solo close-up portrait of Ariela.',
    tone: 'indigo',
  },
  {
    personId: 'p-elizabeth-potthast',
    name: 'Elizabeth Potthast',
    shortName: 'Elizabeth',
    initials: 'EP',
    platform: 'tiktok',
    handle: '@elizabethcastravet',
    profileUrl: 'https://www.tiktok.com/@elizabethcastravet',
    postUrl: 'https://www.tiktok.com/@elizabethcastravet/video/7685795904896437517',
    postId: '7685795904896437517',
    mediaKind: 'video',
    showLabel: '90 Day Fiancé',
    accessibilitySummary: 'A close-up solo portrait of Elizabeth resting her face on one hand.',
    tone: 'coral',
    focusY: 58,
  },
  {
    personId: 'p-kara-bass',
    name: 'Kara Bass',
    shortName: 'Kara',
    initials: 'KB',
    platform: 'tiktok',
    handle: '@karaleona',
    profileUrl: 'https://www.tiktok.com/@karaleona',
    postUrl: 'https://www.tiktok.com/@karaleona/video/7642151959880879373',
    postId: '7642151959880879373',
    mediaKind: 'video',
    showLabel: '90 Day Fiancé',
    accessibilitySummary: 'A polished solo fashion portrait of Kara in a white blazer.',
    tone: 'violet',
  },
  {
    personId: 'p-ed-brown',
    name: 'Ed "Big Ed" Brown',
    shortName: 'Big Ed',
    initials: 'EB',
    platform: 'tiktok',
    handle: '@thisisbiged',
    profileUrl: 'https://www.tiktok.com/@thisisbiged',
    postUrl: 'https://www.tiktok.com/@thisisbiged/video/7582234970710756638',
    postId: '7582234970710756638',
    mediaKind: 'video',
    showLabel: 'Before the 90 Days',
    accessibilitySummary: 'A clear solo close-up portrait of Ed in a black shirt.',
    tone: 'citrus',
  },
  {
    personId: 'p-michael-ilesanmi',
    name: 'Michael Ilesanmi',
    shortName: 'Michael',
    initials: 'MI',
    platform: 'tiktok',
    handle: '@mykol_26',
    profileUrl: 'https://www.tiktok.com/@mykol_26',
    postUrl: 'https://www.tiktok.com/@mykol_26/video/7679097623810411807',
    postId: '7679097623810411807',
    mediaKind: 'video',
    showLabel: 'Before the 90 Days',
    accessibilitySummary: 'A solo full-body indoor portrait of Michael in a blue athletic set.',
    tone: 'rose',
  },
  {
    personId: 'p-andrei-castravet',
    name: 'Andrei Castravet',
    shortName: 'Andrei',
    initials: 'AC',
    platform: 'tiktok',
    handle: '@theandreicastravet',
    profileUrl: 'https://www.tiktok.com/@theandreicastravet',
    postUrl: 'https://www.tiktok.com/@theandreicastravet/video/7689227640632298765',
    postId: '7689227640632298765',
    mediaKind: 'video',
    showLabel: '90 Day Fiancé',
    accessibilitySummary: 'A clear solo close-up portrait of Andrei in a dark shirt against black curtains.',
    tone: 'violet',
    focusY: 44,
  },
  {
    personId: 'p-biniyam-shibre',
    name: 'Biniyam Shibre',
    shortName: 'Biniyam',
    initials: 'BS',
    platform: 'tiktok',
    handle: '@biniyam_shibre',
    profileUrl: 'https://www.tiktok.com/@biniyam_shibre',
    postUrl: 'https://www.tiktok.com/@biniyam_shibre/video/7691249538937556245',
    postId: '7691249538937556245',
    mediaKind: 'video',
    showLabel: 'The Other Way',
    accessibilitySummary: 'A solo outdoor portrait of Biniyam seated on a boat with a bright shoreline behind him.',
    tone: 'indigo',
    focusY: 48,
  },
  {
    personId: 'p-rob-warne',
    name: 'Rob Warne',
    shortName: 'Rob',
    initials: 'RW',
    platform: 'tiktok',
    handle: '@robwarne',
    profileUrl: 'https://www.tiktok.com/@robwarne',
    postUrl: 'https://www.tiktok.com/@robwarne/video/7651039872987221279',
    postId: '7651039872987221279',
    mediaKind: 'video',
    showLabel: '90 Day Fiancé',
    accessibilitySummary: 'A clear solo car portrait of Rob in a blue shirt and gold chain.',
    tone: 'coral',
    focusY: 52,
  },
]

export const castPortraits: CastPortrait[] = [...coreCastPortraits, ...batch50Portraits, ...photoPassPortraits, ...researched2021Portraits, ...octoberPassPortraits]

export function portraitForPerson(personId: string) {
  return castPortraits.find((portrait) => portrait.personId === personId)
}
